import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'

import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import Avatar from '../ui/Avatar'
import UserChallenges from './UserChallenges'
import UserPosts from './UserPosts'
import UserStats from './UserStats'
import {
  Confirm,
  LineSkeleton,
  Meta,
  Note,
  Pill,
  dayOf,
  fullName
} from './peopleUI'
import './people.css'

/*
 * Η καρτέλα ενός χρήστη.
 *
 * Οι κλήσεις στο API είναι ακριβώς οι ίδιες: GET admin/users/:id,
 * POST admin/users με {IsActive,id}, DELETE admin/users/:id.
 *
 * Αυτό που άλλαξε: τα στοιχεία που ο admin δεν μπορεί να αλλάξει δεν είναι πια
 * κλειδωμένα πεδία φόρμας αλλά κείμενο — ένα disabled input λέει «δοκίμασε»,
 * ένα label με την τιμή του λέει την αλήθεια. Το μοναδικό πράγμα που όντως
 * αλλάζει (Ενεργό) έχει δικό του panel και ξεκάθαρο κουμπί, και η διαγραφή
 * ζητάει επιβεβαίωση μέσα στη σελίδα αντί για window.confirm.
 *
 * ΚΑΤΑΣΤΑΣΗ ΚΑΙ ΠΛΟΗΓΗΣΗ — γιατί η φόρμα δεν «κολλάει» ανοιχτή.
 *
 * Το σφάλμα που ανέφερε ο πελάτης (φεύγω από έναν χρήστη, γυρίζω στους Users
 * και βρίσκω ανοιχτή μια άδεια «Νέος User») γεννιόταν από ένα κοινό `form`
 * flag στο UserContext, μοιρασμένο σε δέκα οθόνες. Το Users.js το κρατάει πια
 * τοπικά. Αυτό το αρχείο ελέγχθηκε ώστε να μην μπορεί να το αναπαράγει μόνο
 * του: δεν διαβάζει και δεν γράφει UserContext, sessionStorage, localStorage
 * ούτε module-level μεταβλητή. Ό,τι κρατάει είναι useState μέσα στο component
 * και πεθαίνει με το unmount.
 *
 * Δύο πράγματα κλειδώθηκαν επιπλέον:
 *   - το aliveRef: μια απάντηση του axios που γυρίζει ΑΦΟΥ ο admin πάτησε
 *     «Λίστα Users» δεν ξαναγράφει state σε ξεριζωμένο component
 *   - το leave(): η μοναδική έξοδος από τη φόρμα, καθαρίζει τα πάντα και καλεί
 *     το showForm με ΚΑΙ ΤΑ ΤΡΙΑ ορίσματα — η διαγραφή έστελνε showForm(0,false)
 *     χωρίς SportID, αφήνοντας το SportID της λίστας undefined
 */

export default function UsersForm(props) {
  const [record, setRecord] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [descr, setDescr] = useState('')

  const [note, setNote] = useState(null)
  const [failed, setFailed] = useState(false)
  const [saving, setSaving] = useState(false)
  const [askDelete, setAskDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const aliveRef = useRef(true)

  useEffect(() => {
    aliveRef.current = true
    return () => { aliveRef.current = false }
  }, [])

  /* Αλλαγή χρήστη = καθαρή καρτέλα. Χωρίς αυτό, ένα μήνυμα λάθους ή μια μισή
     επιβεβαίωση διαγραφής από τον προηγούμενο χρήστη επιβίωνε στον επόμενο. */
  useEffect(() => {
    setNote(null)
    setAskDelete(false)
    setFailed(false)
    loadData(props.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.id])

  const loadData = (id) => {
    setIsLoading(true)
    setFailed(false)
    if (id != 0) {
      axios.get(process.env.REACT_APP_API_SERVER + 'admin/users/' + id, {
        headers: {
          'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
        }
      })
        .then(res => {
          if (!aliveRef.current) return
          setRecord(res.data.results)
          setDescr(res.data.results.Descr)
          setIsLoading(false)
        }).catch(err => {
          if (!aliveRef.current) return
          setIsLoading(false)
          setFailed(true)
        })
    } else {
      setRecord({
        id: 0,
        IsActive: 1,
        Descr: ''
      })
      setIsLoading(false)
    }
  }

  /* Η μοναδική έξοδος από τη φόρμα προς τη λίστα. */
  const leave = () => {
    setNote(null)
    setAskDelete(false)
    setRecord({})
    setDescr('')
    if (typeof props.showForm === 'function') props.showForm(0, false, 0)
  }

  const save = (e) => {
    e.preventDefault()
    setSaving(true)
    setNote(null)

    const obj = { IsActive: record.IsActive, id: record.id }

    axios.post(process.env.REACT_APP_API_SERVER + 'admin/users', obj, {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        if (!aliveRef.current) return
        setSaving(false)
        setNote({ tone: 'good', text: res.data.msg })
      }).catch(err => {
        if (!aliveRef.current) return
        setSaving(false)
        setNote({ tone: 'crit', text: 'Something went wrong, please try again' })
      })
  }

  const deleteRecord = () => {
    setDeleting(true)
    setNote(null)

    axios.delete(process.env.REACT_APP_API_SERVER + 'admin/users/' + record.id, {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        if (!aliveRef.current) return
        setDeleting(false)
        if (res.data.err == 0) {
          leave()
        } else {
          setAskDelete(false)
          setNote({ tone: 'crit', text: res.data.msg })
        }
      }).catch(err => {
        if (!aliveRef.current) return
        setDeleting(false)
        setAskDelete(false)
        setNote({ tone: 'crit', text: 'The delete did not go through, please try again' })
      })
  }

  const name = fullName(record.FirstName, record.LastName)
  const isNew = props.id == 0

  /* Τα στοιχεία που δηλώνει ο ίδιος ο χρήστης — μόνο για ανάγνωση. */
  const profileMeta = [
    ['First Name', record.FirstName],
    ['Surname', record.LastName],
    ['Nickname', record.Nickname],
    ['Date of Birth', dayOf(record.bd) || record.bd],
    ['Country', record.Country],
    ['Email', record.Email],
    ['MobilePhone', record.MobilePhone]
  ]

  const sportMeta = [
    ['Team', record.Team],
    ['Previous Teams', record.PreviousTeams],
    ['Position Title', record.PositionTitle],
    ['Favourite Number', record.FavouriteNumber],
    ['Height', record.Height ? record.Height + ' cm' : ''],
    ['Weight', record.Weight ? record.Weight + ' kgr' : '']
  ]

  return (
    <div className="pp-stack">

      {note ? (
        <Note tone={note.tone} onDismiss={() => setNote(null)}>{note.text}</Note>
      ) : null}

      {failed ? (
        <Note tone="crit" onDismiss={() => setFailed(false)}>
          The user profile could not be loaded. Try again from the list.
        </Note>
      ) : null}

      {/* ---------- ΤΑΥΤΟΤΗΤΑ ---------- */}
      <Panel>
        {isLoading ? (
          <LineSkeleton lines={3} />
        ) : (
          <div className="pp-id">
            <Avatar uuid={record.UUID} name={name} size={76} />
            <div className="pp-id-head">
              <div className="pp-id-name">{isNew ? 'New User' : (name || '—')}</div>
              {record.Email ? <div className="pp-id-sub">{record.Email}</div> : null}
              <div className="pp-id-tags">
                {record.IsActive == 1
                  ? <Pill tone="good">Active</Pill>
                  : <Pill tone="crit">Inactive</Pill>}
                <Pill tone={record.ProfileIsPublic == 1 ? 'accent' : 'flat'}>
                  {record.ProfileIsPublic == 1 ? 'PUBLIC PROFILE' : 'FRIENDS ONLY PROFILE'}
                </Pill>
                {record.ParentEmail && record.ParentEmail.length > 0
                  ? <Pill tone="crit">Parent Email</Pill>
                  : null}
              </div>
            </div>
          </div>
        )}
      </Panel>

      {/* ---------- ΣΤΟΙΧΕΙΑ ΧΡΗΣΤΗ (read-only) ---------- */}
      <Panel title="Details" subtitle="Filled in by the user — not editable from here">
        {isLoading ? (
          <LineSkeleton lines={4} />
        ) : (
          <div className="pp-form">
            <Meta items={profileMeta} />

            {record.ParentEmail && record.ParentEmail.length > 0 ? (
              <Note tone="crit">
                <b>Parent Email:</b> {record.ParentEmail}
                <br />
                This account belongs to a minor and is linked to the parent’s address.
              </Note>
            ) : null}

            <fieldset className="pp-fieldset">
              <legend className="pp-legend">Sport profile</legend>
              <Meta items={sportMeta} />
            </fieldset>

            {record.PersonalInfo ? (
              <div className="pp-field">
                <div className="pp-label">Info</div>
                <div className="pp-quote">{record.PersonalInfo}</div>
              </div>
            ) : null}

            {descr ? (
              <div className="pp-field">
                <div className="pp-label">Description</div>
                <div className="pp-quote">{descr}</div>
              </div>
            ) : null}
          </div>
        )}
      </Panel>

      {/* ---------- ΚΑΤΑΣΤΑΣΗ ---------- */}
      <Panel
        title="Account Status"
        subtitle="The only field an admin can change"
      >
        {askDelete ? (
          <Confirm
            title="Delete user"
            confirmLabel="Yes, delete"
            busy={deleting}
            onCancel={() => setAskDelete(false)}
            onConfirm={deleteRecord}
          >
            User <b>{name || record.UUID}</b> will be permanently deleted along with everything
            they have uploaded. This cannot be undone.
          </Confirm>
        ) : (
          <form className="pp-form" onSubmit={save}>
            <div className="pp-grid">
              <div className="pp-field">
                <label className="pp-label" htmlFor="pp-user-active">Active</label>
                <select
                  id="pp-user-active"
                  className="pp-select"
                  value={record.IsActive == 1 ? '1' : '0'}
                  onChange={(e) => setRecord({ ...record, IsActive: e.target.value })}
                >
                  <option value='1'>YES</option>
                  <option value='0'>NO</option>
                </select>
                <div className="pp-hint">
                  An inactive user no longer appears in the app.
                </div>
              </div>
            </div>

            <div className="pp-actions pp-actions-spread">
              {props.id != 0 ? (
                <button type="button" className="btn-ui pp-crit" onClick={() => setAskDelete(true)}>
                  Delete
                </button>
              ) : <span />}

              <Btn variant="accent" type="submit" disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Btn>
            </div>
          </form>
        )}
      </Panel>

      {/* Το όνομα και το UUID περνάνε παρακάτω για τον player: η κεφαλίδα του
          VideoModal δείχνει ποιανού είναι το κλιπ χωρίς να κλείσει ο admin
          τίποτα. Καμία κλήση API δεν αλλάζει — props μόνο. */}
      {props.id != 0 ? (
        <>
          <UserStats id={props.id} SportID={props.SportID} />
          <UserChallenges id={props.id} name={name} uuid={record.UUID || props.id} />
          <UserPosts id={props.id} name={name} uuid={record.UUID || props.id} />
        </>
      ) : null}
    </div>
  )
}
